import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import set from 'lodash/set';
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { History } from 'history';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { Customer } from 'features/Customers/customers';
import { customersPrefix } from 'routes/routes';
import { CustomerFormProps } from './customerFormSchema';
import { axiosInstance } from '../axiosInstance';

type CustomerState = {
  apiErrors: ApiErrors;
  customerDetails?: Customer | undefined;
  loaders: Loaders;
};

interface SaveCustomerParams {
  customer: CustomerFormProps;
  history?: History;
  id?: string;
}

const customersApiPrefix = '/contacts';

// TODO: use object instead of  array
const initialState: CustomerState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  }
};

export const fetchCustomerDetails = createAsyncThunk<Customer, { customerId: string }, { rejectValue: string }>(
  'customerDetails/fetchCustomer',
  async ({ customerId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${customersApiPrefix}/${customerId}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('customers.cannot fetch customer'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('errors.something went wrong'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('customers.not found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const saveCustomer = createAsyncThunk<Customer, SaveCustomerParams, { rejectValue: string }>(
  'customerDetails/saveCustomer',
  async ({ id, customer, history }, { rejectWithValue }) => {
    let response;

    try {
      if (id) {
        response = await axiosInstance.put(`${customersApiPrefix}/${id}`, customer);
      } else {
        response = await axiosInstance.post(`${customersApiPrefix}`, customer);
      }
      const { data } = response;
      if (!id) {
        history?.replace(`${customersPrefix}/details/${data.id}`);
      }
      return {
        ...data
      };
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('customers.you cannot update or create users'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const customersSlice = createSlice({
  initialState,
  name: 'customers',
  reducers: {
    closeError: state => {
      state.apiErrors = {};
    },
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetCustomerForm: state => initialState
  },
  extraReducers: builder =>
    builder
      .addCase(fetchCustomerDetails.fulfilled, (state, { payload }: PayloadAction<Customer>) => {
        state.customerDetails = payload;
        state.loaders.isLoading = false;
      })
      .addCase(fetchCustomerDetails.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchCustomerDetails.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(saveCustomer.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.customerDetails = payload;
      })
      .addCase(saveCustomer.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveCustomer.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = customersSlice;
export { reducer as customerDetails };
export const { closeError, resetCustomerForm, updateListDelete } = actions;

export const getCustomerDetails = (state: RootState) => state.customerDetails.customerDetails;
export const getCustomerDetailsLoaders = (state: RootState) => state.customerDetails.loaders;
export const getCustomerDetailsApiErrors = (state: RootState) => state.customerDetails.apiErrors;
